/**
 * SEO helpers — site-wide constants + JSON-LD builders.
 *
 * Per-route meta tags are rendered via the <SeoHead> component
 * (src/components/seo/SeoHead.tsx). Build JSON-LD here so the
 * serialisation stays consistent and the same object can be unit-tested
 * independently of React.
 */

export const SITE_URL = "https://delapresse.fr";
export const SITE_NAME = "Delapresse";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

import type { Locale } from "@/lib/locale";

export type SupportedLocale = Locale;

export interface BaseSeoInput {
  title: string;
  description: string;
  path: string;
  locale?: SupportedLocale;
  image?: string;
  noindex?: boolean;
}

export interface ArticleSeoInput extends BaseSeoInput {
  publishedAt?: string | null;
  modifiedAt?: string | null;
  authorName?: string | null;
  tags?: string[] | null;
  section?: string | null;
  /** Schema.org article subtype. Defaults to NewsArticle for editorial
   * content (/article/*, /vitrine/*). Use 'Article' for evergreen
   * marketing pages (/comparatif/*) so Google does not expect News
   * indexing signals. */
  articleType?: "NewsArticle" | "Article" | "TechArticle";
  /** True when the article was produced with AI assistance. Adds an explicit
   * software-agent creator to the JSON-LD — the structured-data counterpart of
   * the visible disclosure, per EU AI Act Art. 50. */
  aiGenerated?: boolean;
}

export function absoluteUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const slash = path.startsWith("/") ? "" : "/";
  return `${SITE_URL}${slash}${path}`;
}

export function buildOrganizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/og-default.png`,
    sameAs: [] as string[],
  };
}

export function buildWebSiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: "fr-FR",
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/category/{search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildArticleJsonLd(input: ArticleSeoInput) {
  const url = absoluteUrl(input.path);
  // Google rejects Article/NewsArticle blocks without a datePublished.
  // For evergreen marketing pages that legitimately don't track a publish
  // date, fall back to "now" rounded to the day so the field is present
  // and stable across renders within the same UTC day.
  const todayIso = new Date().toISOString().slice(0, 10);
  return {
    "@context": "https://schema.org",
    "@type": input.articleType ?? "NewsArticle",
    headline: input.title,
    description: input.description,
    image: input.image ? [absoluteUrl(input.image)] : [DEFAULT_OG_IMAGE],
    datePublished: input.publishedAt ?? todayIso,
    dateModified: input.modifiedAt ?? input.publishedAt ?? todayIso,
    author: input.authorName
      ? { "@type": "Person", name: input.authorName }
      : { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/og-default.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    articleSection: input.section ?? undefined,
    keywords: input.tags && input.tags.length > 0 ? input.tags.join(", ") : undefined,
    inLanguage: input.locale === "en" ? "en-US" : "fr-FR",
    // EU AI Act Art. 50 — machine-readable AI-generation signal. A SoftwareApplication
    // creator alongside the human editorial author reflects the assisted workflow.
    creator: input.aiGenerated
      ? { "@type": "SoftwareApplication", name: `${SITE_NAME} AI`, applicationCategory: "AI content assistant" }
      : undefined,
  };
}

export function buildBreadcrumbJsonLd(crumbs: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  };
}

export interface FAQItem {
  question: string;
  answer: string;
}

export function buildFAQPageJsonLd(items: FAQItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.question,
      acceptedAnswer: { "@type": "Answer", text: it.answer },
    })),
  };
}

export interface ProductOffer {
  price: number;
  priceCurrency: string;
  url?: string;
  availability?: "InStock" | "PreOrder" | "OutOfStock";
}

export interface ProductJsonLdInput {
  name: string;
  description: string;
  image?: string;
  brand?: string;
  category?: string;
  offers?: ProductOffer | ProductOffer[];
}

function shapeOffer(o: ProductOffer) {
  return {
    "@type": "Offer",
    price: o.price.toFixed(2),
    priceCurrency: o.priceCurrency,
    availability: `https://schema.org/${o.availability ?? "InStock"}`,
    url: o.url ? absoluteUrl(o.url) : undefined,
  };
}

export function buildProductJsonLd(input: ProductJsonLdInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: input.name,
    description: input.description,
    image: input.image ? absoluteUrl(input.image) : DEFAULT_OG_IMAGE,
    brand: { "@type": "Brand", name: input.brand ?? SITE_NAME },
    category: input.category,
    offers: Array.isArray(input.offers)
      ? input.offers.map(shapeOffer)
      : input.offers
        ? shapeOffer(input.offers)
        : undefined,
  };
}

export interface HowToStep {
  name: string;
  text: string;
  url?: string;
}

export interface HowToJsonLdInput {
  name: string;
  description: string;
  totalTimeISO?: string;
  steps: HowToStep[];
}

export function buildHowToJsonLd(input: HowToJsonLdInput) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: input.name,
    description: input.description,
    totalTime: input.totalTimeISO,
    step: input.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: s.url ? absoluteUrl(s.url) : undefined,
    })),
  };
}

export interface ContactPointJsonLdInput {
  email?: string;
  telephone?: string;
  contactType?: string;
  areaServed?: string | string[];
  availableLanguage?: string | string[];
  url?: string;
}

export function buildContactPointJsonLd(input: ContactPointJsonLdInput) {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPoint",
    email: input.email,
    telephone: input.telephone,
    contactType: input.contactType ?? "customer support",
    areaServed: input.areaServed,
    availableLanguage: input.availableLanguage,
    url: input.url ? absoluteUrl(input.url) : undefined,
  };
}

export function buildItemListJsonLd<T>(
  items: T[],
  shape: (item: T, index: number) => Record<string, unknown>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: shape(it, i),
    })),
  };
}
